// =============================================================================
// PAGES/CONECTARISOLVE.TSX - Tela de colagem assistida do cookie iSolve (SPEC 1, 4b)
// =============================================================================
// Qualquer usuário autenticado+ativo pode conectar a sua própria sessão iSolve.
// O front NUNCA decodifica o JWT, NUNCA exibe o cookie de volta, NUNCA loga o valor.
// Quem lê exp/personUUID é o backend (sessao-isolve.service).
// =============================================================================

import { useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

type StatusSessao = 'NUNCA_CONECTADA' | 'ATIVA' | 'EXPIRADA';

interface RespostaStatus {
  status: StatusSessao;
  expEm: string | null;
}

function formatarData(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function BadgeStatus({ status, expEm }: { status: StatusSessao; expEm: string | null }) {
  if (status === 'ATIVA') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        Conectado - expira em {formatarData(expEm)}
      </span>
    );
  }
  if (status === 'EXPIRADA') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
        <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
        Sessão expirada - reconecte abaixo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-500">
      <span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />
      Não conectado
    </span>
  );
}

export default function ConectarISolve() {
  const [statusSessao, setStatusSessao] = useState<RespostaStatus | null>(null);
  const [carregandoStatus, setCarregandoStatus] = useState(true);
  const [cookie, setCookie] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function carregarStatus() {
    setCarregandoStatus(true);
    try {
      const { data } = await api.get<RespostaStatus>('/isolve/sessao/minha');
      setStatusSessao(data);
    } catch {
      setStatusSessao({ status: 'NUNCA_CONECTADA', expEm: null });
    } finally {
      setCarregandoStatus(false);
    }
  }

  useEffect(() => { carregarStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function validarPareceCookieJWT(valor: string): boolean {
    // Validação mínima no front: 3 segmentos separados por ponto, comprimento plausível
    const partes = valor.split('.');
    return partes.length === 3 && valor.length > 100;
  }

  async function handleConectar() {
    // Trim apenas nas pontas - nunca alterar o miolo do valor
    const valorTrimado = cookie.trim();

    if (!validarPareceCookieJWT(valorTrimado)) {
      toast.error('Isso não parece um cookie válido. Confira se copiou o Value inteiro do 5AJWT_AUTH.');
      return;
    }

    setSalvando(true);
    try {
      const { data } = await api.post<RespostaStatus>('/isolve/sessao/minha', { cookie: valorTrimado });
      setStatusSessao(data);
      toast.success(`Conectado - sessão válida até ${formatarData(data.expEm)}.`);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao salvar sessão.';
      toast.error(msg.replace(/cookie/gi, 'sessão')); // não propaga a palavra "cookie" em mensagens de erro
    } finally {
      setSalvando(false);
      // Limpar o campo imediatamente após o envio - o valor não fica no estado
      setCookie('');
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-800 mb-1">Conectar ao iSolve</h1>
      <p className="text-sm text-slate-500 mb-6">
        Configure sua sessão pessoal do iSolve para sincronizar propostas do Quinto Andar.
      </p>

      {/* 1. ESTADO ATUAL */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Status da sua sessão</h2>
        {carregandoStatus ? (
          <span className="text-sm text-slate-400">Verificando…</span>
        ) : statusSessao ? (
          <BadgeStatus status={statusSessao.status} expEm={statusSessao.expEm} />
        ) : null}
      </div>

      {/* 2. INSTRUÇÃO PASSO A PASSO */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-5">
        <h2 className="text-sm font-semibold text-blue-800 mb-3">Como capturar o cookie do iSolve</h2>
        <ol className="space-y-2 text-sm text-blue-900">
          <li className="flex gap-2">
            <span className="font-bold shrink-0">1.</span>
            Abra o iSolve no navegador e certifique-se de estar <strong>logado</strong>.
          </li>
          <li className="flex gap-2">
            <span className="font-bold shrink-0">2.</span>
            Pressione <kbd className="bg-white border border-blue-300 rounded px-1 py-0.5 font-mono text-xs">F12</kbd> para abrir as Ferramentas do Desenvolvedor.
          </li>
          <li className="flex gap-2">
            <span className="font-bold shrink-0">3.</span>
            Clique na aba <strong>Application</strong> (pode estar em um menu "»" se a janela for pequena).
          </li>
          <li className="flex gap-2">
            <span className="font-bold shrink-0">4.</span>
            No painel esquerdo, expanda <strong>Cookies</strong> e clique no domínio do iSolve.
          </li>
          <li className="flex gap-2">
            <span className="font-bold shrink-0">5.</span>
            Localize o cookie chamado <strong className="font-mono">5AJWT_AUTH</strong>.
          </li>
          <li className="flex gap-2">
            <span className="font-bold shrink-0">6.</span>
            Clique nele e copie o conteúdo da coluna <strong>Value</strong> - o texto inteiro, sem espaços extras.
          </li>
        </ol>
        <p className="mt-3 text-xs text-blue-700">
          Atenção: copie o <strong>Value</strong> do <strong>5AJWT_AUTH</strong> - não outro cookie da lista.
        </p>
      </div>

      {/* 3. CAMPO DE COLAGEM + BOTÃO */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Cole o cookie aqui</h2>
        <textarea
          value={cookie}
          onChange={(e) => setCookie(e.target.value)}
          placeholder="Cole aqui o Value do cookie 5AJWT_AUTH…"
          rows={4}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          disabled={salvando}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={handleConectar}
            disabled={salvando || cookie.trim().length === 0}
            className="px-4 py-2 bg-otm-orange text-white text-sm font-medium rounded-lg hover:bg-otm-orange-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {salvando ? 'Conectando…' : 'Conectar'}
          </button>
          {cookie.trim().length > 0 && (
            <button
              onClick={() => setCookie('')}
              disabled={salvando}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Limpar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
